(() => {
  const KEY = 'precioux_wishlist_v1';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
    window.dispatchEvent(new CustomEvent('precioux:wishlist-change', { detail: items }));
  };

  const showToast = (message, viewLink = '/pages/wishlist') => {
    let toast = document.getElementById('precioux-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'precioux-wishlist-toast';
      toast.className = 'precioux-wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.innerHTML = `
      <div class="precioux-wishlist-toast__content">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" fill="#dc2626" stroke="#dc2626"/>
        </svg>
        <span>${message}</span>
      </div>
      <a href="${viewLink}" class="precioux-wishlist-toast__btn">View Wishlist</a>
    `;
    toast.classList.add('is-visible');
    clearTimeout(window.__preciouxToastTimer);
    window.__preciouxToastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 4500);
  };

  const syncCount = (items = read()) => {
    document.querySelectorAll('.header-actions__wishlist, [data-wishlist-header-link]').forEach((link) => {
      let badge = link.querySelector('.precioux-wishlist-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'precioux-wishlist-count';
        link.appendChild(badge);
      }
      badge.textContent = items.length > 99 ? '99+' : String(items.length);
      badge.hidden = items.length === 0;
      badge.style.display = items.length === 0 ? 'none' : 'grid';
      link.setAttribute('aria-label', `Wishlist, ${items.length} saved item${items.length === 1 ? '' : 's'}`);
    });
  };

  const syncButtons = (items = read()) => {
    const ids = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const pId = String(button.dataset.productId || '');
      const active = Boolean(pId && ids.has(pId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const title = button.dataset.productTitle || 'Product';
      button.setAttribute('aria-label', `${active ? 'View wishlist (' : 'Add '}${title}${active ? ' saved)' : ' to wishlist'}`);
      const label = button.querySelector('[data-wishlist-label]');
      if (label) {
        label.textContent = active ? 'Saved in wishlist' : 'Add to wishlist';
      }
    });
  };

  // Add an item through the theme's native cart event pipeline. This keeps
  // the drawer contents, totals, and header cart bubble in sync.
  window.addWishlistItemToCart = async (variantId, buttonEl) => {
    if (!variantId || !buttonEl) return;

    const origHTML = buttonEl.innerHTML;
    buttonEl.disabled = true;
    buttonEl.innerHTML = '<span>Adding...</span>';

    let deferredEventPromise;
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        )
      ];

      deferredEventPromise = CartLinesUpdateEvent.createPromise();
      buttonEl.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'wishlist',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferredEventPromise.promise
        })
      );

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const error = new Error(result.description || result.message || 'This item is unavailable');
        deferredEventPromise.reject(error);
        throw error;
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!cartResponse.ok) throw new Error('The cart could not be refreshed');
      const ajaxCart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'precioux-wishlist',
          sourceId: 'precioux-wishlist',
          itemCount: 1,
          sections: result.sections,
          didError: false
        }
      });

      buttonEl.innerHTML = '<span>Added to Cart!</span>';
      buttonEl.classList.add('is-added');
    } catch (error) {
      console.error('Cart add error:', error);
      if (deferredEventPromise) deferredEventPromise.reject(error);
      buttonEl.innerHTML = `<span>${error.message || 'Unable to add'}</span>`;
    } finally {
      setTimeout(() => {
        buttonEl.disabled = false;
        buttonEl.innerHTML = origHTML;
        buttonEl.classList.remove('is-added');
      }, 2500);
    }
  };

  const renderPage = (items = read()) => {
    const grid = document.querySelector('[data-wishlist-grid]');
    const empty = document.querySelector('[data-wishlist-empty]');
    const countHeader = document.querySelector('[data-wishlist-total-count]');
    if (!grid) return;

    if (countHeader) {
      countHeader.textContent = `(${items.length})`;
    }

    grid.replaceChildren();
    if (empty) empty.hidden = items.length > 0;

    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'precioux-wishlist-card';

      const imageLink = document.createElement('a');
      imageLink.href = item.url || '#';
      imageLink.className = 'precioux-wishlist-card__image';
      const image = document.createElement('img');
      image.src = item.image || '';
      image.alt = item.title || 'Product';
      image.loading = 'lazy';
      imageLink.appendChild(image);

      const body = document.createElement('div');
      body.className = 'precioux-wishlist-card__body';

      const title = document.createElement('a');
      title.href = item.url || '#';
      title.className = 'precioux-wishlist-card__title';
      title.textContent = item.title || 'Piece';

      const price = document.createElement('p');
      price.className = 'precioux-wishlist-card__price';
      price.textContent = item.price || '';

      const actions = document.createElement('div');
      actions.className = 'precioux-wishlist-card__actions';

      // 1. Buy now / Add to cart button
      const buyBtn = document.createElement('button');
      buyBtn.type = 'button';
      buyBtn.className = 'precioux-wishlist-card__buy-btn';
      buyBtn.innerHTML = `
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
        </svg>
        <span>Add to Cart</span>
      `;
      buyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.addWishlistItemToCart(item.variantId || item.id, buyBtn);
      });

      // 2. View details link
      const view = document.createElement('a');
      view.href = item.url || '#';
      view.className = 'precioux-wishlist-card__view';
      view.textContent = 'View piece';

      // 3. Remove button
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'precioux-wishlist-card__remove';
      remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      remove.innerHTML = '&times; Remove';
      remove.addEventListener('click', () => {
        write(read().filter((saved) => String(saved.id) !== String(item.id)));
      });

      actions.append(buyBtn, view, remove);
      body.append(title, price, actions);
      card.append(imageLink, body);
      grid.appendChild(card);
    });
  };

  // Main Wishlist Toggle Function
  window.togglePreciouxWishlist = function(button, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!button) return;

    const items = read();
    
    // Extract Product ID
    let id = button.dataset.productId;
    if (!id || id === 'undefined' || id === 'null') {
      const comp = button.closest('product-form-component, [data-product-id]');
      id = comp?.dataset?.productId || document.querySelector('[name="product-id"]')?.value || window.location.pathname.split('/').pop();
    }
    id = String(id || '');

    // Extract Variant ID
    let variantId = button.dataset.variantId || id;
    const formVariantInput = button.closest('product-form-component, form')?.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
    if (formVariantInput && formVariantInput.value) {
      variantId = formVariantInput.value;
    }

    // Extract Title
    let title = button.dataset.productTitle;
    if (!title || title === 'undefined') {
      title = document.querySelector('.product__title h1, h1.product-title, .product-title, h1')?.textContent?.trim() || 'Precioux Piece';
    }

    // Extract URL
    let url = button.dataset.productUrl;
    if (!url || url === 'undefined') {
      url = window.location.pathname;
    }

    // Extract Image
    let image = button.dataset.productImage;
    if (!image || image === 'undefined') {
      image = document.querySelector('.product-media img, .product__media img, [data-media-id] img, .product-gallery img')?.src || '';
    }

    // Extract Price
    let price = button.dataset.productPrice;
    if (!price || price === 'undefined') {
      price = document.querySelector('.price-item--regular, .product-price, .price, .price-item')?.textContent?.trim() || '';
    }

    const existingIndex = items.findIndex((item) => String(item.id) === id);

    if (existingIndex >= 0) {
      // If already saved in wishlist, pressing the button directs user to their wishlist page to see saved products
      window.location.href = '/pages/wishlist';
    } else {
      // Add product to wishlist WITHOUT redirecting
      items.unshift({
        id,
        variantId,
        title,
        url,
        image,
        price
      });
      write(items);

      // Show toast alert so they can continue browsing or tap View Wishlist
      showToast(`Added to your wishlist!`);
    }
  };

  // Capture phase listener to prevent parent forms from blocking clicks
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    window.togglePreciouxWishlist(button, event);
  }, true);

  const sync = (event) => {
    const items = event?.detail || read();
    syncCount(items);
    syncButtons(items);
    renderPage(items);
  };

  window.addEventListener('precioux:wishlist-change', sync);
  window.addEventListener('storage', sync);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
